"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.toMasterContentResourceId = exports.assertMasterContentResourceId = exports.isMasterContentResourceId = exports.assertLocalisedContentResourceId = exports.isLocalisedContentResourceId = exports.assertContentResourceId = exports.isResourceId = exports.isContentResourceId = exports.parseContentTypeFromResourceId = exports.parseResourceId = void 0;
var models_1 = require("../models");
function mapMatchesToResponse(matches) {
    return {
        partition: matches[1],
        subpartition: matches[3],
        guid: matches[4],
        locale: matches[7],
        language: matches[8],
        region: matches[9],
    };
}
/**
 * Parses the given id into its components. Will throw an error in case the specified id is not of the correct format.
 * @param {string} contentResourceId the id to parse
 * @return {ResourceIdParsed} the resource id components
 */
function parseResourceId(contentResourceId) {
    if (!contentResourceId) {
        throw new Error("Cannot parse id: '" + contentResourceId + "' make sure it has the correct format of a content resource id and contains a real uuid.");
    }
    var matches = contentResourceId.match(models_1.CONTENT_OR_MASTER_RESOURCE_ID_PATTERN);
    if (!matches) {
        throw new Error("Cannot parse id: " + contentResourceId + " make sure it has the correct format of a content resource id and contains a real uuid.");
    }
    else {
        return mapMatchesToResponse(matches);
    }
}
exports.parseResourceId = parseResourceId;
/**
 * Tries to derive the content type from a content resource id, supports localized content resources, master resources
 * as well as featuring records and bundles.
 * @param {string} contentResourceId the content resource id
 * @return {string} the content type of the resource, if supported
 */
function parseContentTypeFromResourceId(contentResourceId) {
    var parsedId = parseResourceId(contentResourceId);
    if (parsedId.partition === 'content') {
        if (!parsedId.locale) {
            // we assume it is a master type
            return "rrn:master:" + parsedId.subpartition;
        }
        else {
            return "rrn:content:" + parsedId.subpartition;
        }
    }
    else if (!parsedId.subpartition) {
        return "rrn:" + parsedId.partition;
    }
    else {
        throw new Error("unsupported content resource id " + contentResourceId);
    }
}
exports.parseContentTypeFromResourceId = parseContentTypeFromResourceId;
/**
 * Checks if the given id is a *content* resource id. It will never throw an error but will return `false` also for
 * valid featuring record ids as well as for invalid ids.
 * @param {string} id the id to inspect
 * @return {boolean} `true` if the id is a valid *content* resource id, `false` for other valid resource ids
 * (featuring-records) as well as for invalid ids
 */
function isContentResourceId(id) {
    try {
        var parsedId = parseResourceId(id);
        return parsedId.partition === 'content';
    }
    catch (e) {
        return false;
    }
}
exports.isContentResourceId = isContentResourceId;
/**
 * Checks if the given id is a valid resource id, i.e. it has the correct format.
 * @param {string} id the id to inspect
 * @return {boolean} `true` if the id is a valid resource id (e.g. content resource or featuring record, ...),
 * `false` otherwise (invalid and random strings)
 */
function isResourceId(id) {
    try {
        var parsedId = parseResourceId(id);
        return !!parsedId;
    }
    catch (e) {
        return false;
    }
}
exports.isResourceId = isResourceId;
/**
 * Checks whether the given is is a *content* resource id and throws an error otherwise. It will also throw an error if
 * the id is a valid featuring record id, for example.
 * @param {string} id the id to inspect
 */
function assertContentResourceId(id) {
    var parsedId = parseResourceId(id);
    if (!parsedId) {
        throw new Error("Cannot parse id: " + id + " make sure it has the correct format of a content resource id and contains a real uuid.");
    }
    if (parsedId.partition !== 'content') {
        throw new Error("The resource id: " + id + " is not a content resource id.");
    }
}
exports.assertContentResourceId = assertContentResourceId;
/**
 * Checks whether the specified id is a localized content resource id. Will return `false` for invalid ids as well as
 * master ids.
 * @param {string} contentResourceId the id to inspect
 */
function isLocalisedContentResourceId(contentResourceId) {
    if (!isContentResourceId(contentResourceId)) {
        return false;
    }
    var parsedId = parseResourceId(contentResourceId);
    return !!(parsedId && parsedId.locale);
}
exports.isLocalisedContentResourceId = isLocalisedContentResourceId;
/**
 * /**
 * Ensures that the given id is a localized content resource id. If it is not or the id is not valid, this method
 * will throw an error.
 * @param {string} contentResourceId the resource id to inspect
 */
function assertLocalisedContentResourceId(contentResourceId) {
    assertContentResourceId(contentResourceId);
    var parsedId = parseResourceId(contentResourceId);
    if (!parsedId) {
        throw new Error("Cannot parse id: " + contentResourceId + " make sure it has the correct format of a content resource id and contains a real uuid.");
    }
    if (!parsedId.locale) {
        throw new Error("The resource id: " + contentResourceId + " is not a localized resource id.");
    }
}
exports.assertLocalisedContentResourceId = assertLocalisedContentResourceId;
/**
 * Checks whether the specified id is a master resource id, i.e. it will return `false` for localized resource ids
 * as well as for invalid ids
 * @param {string} contentResourceId the id to inspect
 * @return {boolean} `true` in case the resource is a valid master resource id without a locale, `false` otherwise
 * (e.g. also for invalid ids)
 */
function isMasterContentResourceId(contentResourceId) {
    if (!isContentResourceId(contentResourceId)) {
        return false;
    }
    var parsedId = parseResourceId(contentResourceId);
    return parsedId && !parsedId.locale;
}
exports.isMasterContentResourceId = isMasterContentResourceId;
/**
 * Checks whether the specified id is a master resource id, i.e. it will throw an error `false` for localized resource ids
 * as well as for invalid ids
 * @param {string} contentResourceId the id to inspect
 */
function assertMasterContentResourceId(contentResourceId) {
    var parsedId = parseResourceId(contentResourceId);
    if (!parsedId) {
        throw new Error("Cannot parse id: " + contentResourceId + " make sure it has the correct format of a content resource id and contains a real uuid.");
    }
    if (parsedId.partition !== 'content') {
        throw new Error("The resource id: " + contentResourceId + " is not a master resource id.");
    }
    if (parsedId.locale) {
        throw new Error("The resource id: " + contentResourceId + " is not a master resource id.");
    }
}
exports.assertMasterContentResourceId = assertMasterContentResourceId;
/**
 * Calculates the master resource id for a given localized resource id.
 * @param {string} contentResourceId the id to inspect
 * @return {string} the corresponding master resource id for a localized resource id, in case you already specified a
 *  master resource id, the same id will be returned.
 */
function toMasterContentResourceId(contentResourceId) {
    assertContentResourceId(contentResourceId);
    var parsed = parseResourceId(contentResourceId);
    return "rrn:" + parsed.partition + ":" + parsed.subpartition + ":" + parsed.guid;
}
exports.toMasterContentResourceId = toMasterContentResourceId;
//# sourceMappingURL=parse-resource-id.util.js.map